import type { ReactiveController, ReactiveControllerHost } from 'lit'

export interface FocusableWhenDisabledOptions {
  /** Reads the host's disabled state; re-read after every host update. */
  disabled: () => boolean
}

/**
 * Keeps an interactive host focusable while disabled, mirroring Base UI's
 * accessible-disabled pattern: a disabled control stays in the tab order
 * (`tabindex="0"`) with `aria-disabled="true"`, rather than being removed from
 * it (`tabindex="-1"`), so assistive-technology users can still discover it.
 * Activation must be blocked by the host (it should no-op its click/key handlers
 * while disabled).
 *
 * The host stays the focusable element (these are light-DOM `role="button"`
 * elements that can't delegate focus into a shadow `<button>`).
 *
 * @example
 * ```ts
 * private focusable = new FocusableWhenDisabledController(this, { disabled: () => this.disabled })
 * ```
 */
export class FocusableWhenDisabledController implements ReactiveController {
  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private options: FocusableWhenDisabledOptions,
  ) {
    host.addController(this)
  }

  hostConnected() {
    this.sync()
  }

  hostUpdated() {
    this.sync()
  }

  private sync() {
    // Always focusable — the element is a button, in the tab order whether or not
    // it is disabled (Base UI behaviour).
    if (this.host.getAttribute('tabindex') !== '0') {
      this.host.setAttribute('tabindex', '0')
    }

    const disabled = this.options.disabled()
    if (disabled) {
      if (this.host.getAttribute('aria-disabled') !== 'true') {
        this.host.setAttribute('aria-disabled', 'true')
      }
    }
    else if (this.host.hasAttribute('aria-disabled')) {
      this.host.removeAttribute('aria-disabled')
    }
  }
}
